import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { City } from "../../types/Country";

interface CitiesResponse {
  data: City[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useCities = (countryId: number | null) => {
  return useQuery({
    queryKey: ["cities", countryId],
    queryFn: async (): Promise<City[]> => {
      if (!countryId) return [];
      const response = await USERAXIOS.get<CitiesResponse>(
        `/countries?parent_id=${countryId}`
      );
      return response.data.data;
    },
    enabled: !!countryId, // Only run query when countryId is provided
  });
};

export const useCitiesInfinite = (
  countryId: number | null,
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["cities", "infinite", countryId, pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<CitiesResponse> => {
      if (!countryId) {
        return {
          data: [],
          meta: { current_page: 1, last_page: 1, per_page: pageSize, total: 0 },
        };
      }
      const response = await USERAXIOS.get<CitiesResponse>(`/countries`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          parent_id: countryId,
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: !!countryId,
  });
};

export const useCitiesForAutocomplete = (
  countryId: number | null,
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useCitiesInfinite(countryId, pageSize, searchTerm);

  // Flatten all pages into a single array
  const cities = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: cities,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
